import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui-kit/accordion';
import { FormLabel } from '@/components/ui-kit/form';
import { Switch } from '@/components/ui-kit/switch';
import { Label } from '@/components/ui-kit/label';
import { InfoTooltip } from '@/components/ui-kit/info-tool-tip';
import { AgentBehaviorSettingsForm } from '@/modules/gpt-chats/components/agents-configuration/components/config-card-section/utils';
import { KnowledgeRecallModal } from './knowledge-recall-modal';
import { Button } from '@/components/ui-kit/button';

import { useGetFolderList } from '@/modules/gpt-chats/hooks/use-kb';
import { AgentConfigAddKBModal } from './agents-config-add-kb-modal';
import { AgentConfigFolderCard } from './agents-config-folder-card';
import { IFolder } from '@/modules/gpt-chats/types/kb.service.type';

interface KnowledgeBaseSectionProps {
  form: UseFormReturn<AgentBehaviorSettingsForm>;
  isMobile: boolean;
  onSubmit?: (values: AgentBehaviorSettingsForm) => Promise<void>;
}

export const KnowledgeBaseSection = ({ form, isMobile, onSubmit }: KnowledgeBaseSectionProps) => {
  const { t } = useTranslation();
  const [isKnowledgeRecallModalOpen, setIsKnowledgeRecallModalOpen] = useState(false);
  const [isAddKBModalOpen, setIsAddKBModalOpen] = useState(false);

  const { data: folderlistData } = useGetFolderList();
  const folders = folderlistData?.data?.folders || [];

  const scoreThreshold = form.watch('rag_config.score_threshold');
  const topK = form.watch('rag_config.top_k');
  const denseWeightValue = form.watch('rag_config.dense_weight') ?? 0.9;
  const keywordWeightValue = form.watch('rag_config.keyword_weight') ?? 0.1;
  const folderIds = form.watch('folder_ids') || [];
  const enableRag = form.watch('enable_rag');

  const semanticPercentage = Math.round(denseWeightValue * 100);
  const keywordPercentage = Math.round(keywordWeightValue * 100);
  const searchType = denseWeightValue > 0.9 ? 'SEMANTIC' : 'MIXED';

  const handleEnableRagChange = async (val: boolean) => {
    form.setValue('enable_rag', val);
    form.setValue('folder_ids', []);
    if (onSubmit) {
      await onSubmit(form.getValues());
    }
  };

  const handleAddKB = async (id: string) => {
    const newIds = [...folderIds, id];
    form.setValue('folder_ids', newIds, { shouldValidate: true, shouldDirty: true });

    // Auto-enable RAG if first folder is added
    if (!enableRag && newIds.length > 0) {
      form.setValue('enable_rag', true);
    }

    if (onSubmit) {
      await onSubmit(form.getValues());
    }
  };

  const handleRemoveKB = async (id: string) => {
    const newIds = folderIds.filter((folderId) => folderId !== id);
    form.setValue('folder_ids', newIds, { shouldValidate: true, shouldDirty: true });

    if (onSubmit) {
      await onSubmit(form.getValues());
    }
  };

  const selectedFolders = folders.filter((folder: IFolder) => folderIds.includes(folder._id));

  return (
    <AccordionItem value="prompt">
      <div className="relative">
        <AccordionTrigger className="px-5 text-lg hover:no-underline">
          <div className="flex w-full items-center justify-between pr-4">
            <Label className="text-lg">{t('KNOWLEDGE_BASE')}</Label>
          </div>
        </AccordionTrigger>

        <div className="absolute right-12 top-1/2 z-10 flex -translate-y-1/2 items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setIsAddKBModalOpen(true);
            }}
            className="gap-2"
             disabled={!enableRag}
          >
            <span className="text-lg">+</span>
            <span>{t('ADD')}</span>
          </Button>

          <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <Switch
              id="knowledge-base-toggle"
              checked={form.watch('enable_rag')}
              onCheckedChange={handleEnableRagChange}
              // size="sm"
            />
          </div>
        </div>
      </div>
      <AccordionContent className="px-5">
        <div className="grid gap-6">
          <div className="flex w-full items-center justify-between xl:w-1/2">
            <div className="flex items-center gap-2">
              <FormLabel className="text-sm font-medium text-medium-emphasis">
                {t('QUESTION_ENHANCEMENT')}
              </FormLabel>
              <InfoTooltip content={t('QUESTION_ENHANCEMENT_TOOLTIP')} />
            </div>
            <Switch
              checked={form.watch('rag_config.enable_query_enhancement')}
              onCheckedChange={(checked) =>
                form.setValue('rag_config.enable_query_enhancement', checked)
              }
              // size="sm"
            />
          </div>

          <div className="relative">
            <div className="mb-3 flex items-center gap-2">
              <FormLabel className="text-sm font-medium">{t('KNOWLEDGE_RECALL')}</FormLabel>
              <InfoTooltip content={t('KNOWLEDGE_RECALL_TOOLTIP')} />
            </div>

            <div className="flex flex-wrap gap-4" id="knowledge-recall-buttons">
              <div
                className="w-fit cursor-pointer rounded-lg bg-secondary px-3 py-2 transition-colors hover:bg-primary/10 lg:px-2 lg:py-1.5 xl:px-3 xl:py-2"
                onClick={() => setIsKnowledgeRecallModalOpen(true)}
              >
                <div className="flex items-center gap-2 lg:gap-1 xl:gap-2">
                  <span className="text-sm text-high-emphasis lg:text-xs xl:text-sm">
                    {t('KNOWLEDGE_RELEVANCE')}:
                  </span>
                  <span className="text-sm font-semibold text-primary lg:text-xs xl:text-sm">
                    {scoreThreshold?.toFixed(2) || '0.70'}
                  </span>
                </div>
              </div>

              <div
                className="w-fit cursor-pointer rounded-lg bg-secondary px-3 py-2 transition-colors hover:bg-primary/10 lg:px-2 lg:py-1.5 xl:px-3 xl:py-2"
                onClick={() => setIsKnowledgeRecallModalOpen(true)}
              >
                <div className="flex items-center gap-2 lg:gap-1 xl:gap-2">
                  <span className="text-sm text-high-emphasis lg:text-xs xl:text-sm">
                    {t('RECALL_NUMBER')}:
                  </span>
                  <span className="text-sm font-semibold text-primary lg:text-xs xl:text-sm">
                    {topK || 10}
                  </span>
                </div>
              </div>

              <div
                className="w-fit cursor-pointer rounded-lg bg-secondary px-3 py-2 transition-colors hover:bg-primary/10 lg:px-2 lg:py-1.5 xl:px-3 xl:py-2"
                onClick={() => setIsKnowledgeRecallModalOpen(true)}
              >
                <div className="flex items-center gap-2 lg:gap-1 xl:gap-2">
                  <span className="text-sm text-high-emphasis lg:text-xs xl:text-sm">
                    {t('SEARCH_WEIGHT')}:
                  </span>
                  <span className="text-sm font-semibold text-primary lg:text-xs xl:text-sm">
                    {t('SEARCH_WEIGHT_DISPLAY', {
                      type: t(searchType),
                      semantic: semanticPercentage,
                      keyword: keywordPercentage,
                    })}
                  </span>
                </div>
              </div>
            </div>

            {selectedFolders.length > 0 && (
              <div className="grid gap-4 mt-4 max-h-[40vh] overflow-y-auto">
                {selectedFolders.map((folder: IFolder) => (
                  <AgentConfigFolderCard
                    key={folder._id}
                    folder={folder}
                    onRemove={handleRemoveKB}
                  />
                ))}
              </div>
            )}

            {isKnowledgeRecallModalOpen && (
              <KnowledgeRecallModal
                form={form}
                onClose={() => setIsKnowledgeRecallModalOpen(false)}
                isMobile={isMobile}
              />
            )}
          </div>
        </div>
      </AccordionContent>

      <AgentConfigAddKBModal
        open={isAddKBModalOpen}
        onOpenChange={setIsAddKBModalOpen}
        currentKbIds={folderIds}
        onAddKB={handleAddKB}
        onRemoveKB={handleRemoveKB}
      />
    </AccordionItem>
  );
};
